import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useDisclosure,
} from '@chakra-ui/react';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/km';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import SelectValueFormField from '../../../../components/form/SelectValueFormField';
import c from '../../../../constant';
import useStore from '../../../../store';
import { mappingKhmerNumber } from '../../../../utils/market-development';
import CompanyField from '../ews-company-field';
import CompanyTypeField from '../ews-company-type-field';
import { riskLevels } from '../../utils';

const validationSchema = Yup.object().shape({
	companyType: Yup.string().nullable(),
	companyId: Yup.string().nullable(),
	year: Yup.string().nullable(),
	level: Yup.string().nullable(),
});

export default function RiskIndicatorFilter({ companyType = null, companyId = null, year = null, level = null, onCallback = () => null }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<>
			<Button
				isLoading={false}
				isDisabled={false}
				onClick={onOpen}
				leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
				colorScheme={companyType || companyId || year || level ? 'yellow' : 'gray'}
				variant="solid"
				size="sm"
				borderRadius={c.borderRadius}
				border="1px solid #bdc3c7"
			>
				<Text fontSize="sm" fontWeight="semibold">
					{t('Filter')}
				</Text>
			</Button>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							validationSchema={validationSchema}
							initialValues={{
								companyType: companyType ?? '',
								companyId: companyId ?? '',
								year: year ?? '',
								level: level ?? '',
							}}
							onSubmit={(values) => {
								let url = '?page=1';

								if (values?.companyType) {
									url = url + `&companyType=${values?.companyType}`;
								}

								if (values?.companyId) {
									url = url + `&companyId=${values?.companyId}`;
								}

								if (values?.year) {
									url = url + `&year=${values?.year}`;
								}

								if (values?.level) {
									url = url + `&level=${values?.level}`;
								}

								onCallback(url);
								onClose();
							}}
						>
							{({ values, setFieldValue, ...props }) => {
								return (
									<Box pb="4">
										<Form>
											<SimpleGrid columns={1} spacing={4}>
												<CompanyTypeField
													{...props}
													name="companyType"
													companyFormName="companyId"
													label="Company Type"
													placeholder={t('Company Type')}
												/>
												<CompanyField {...props} name="companyId" companyTypeFormName="companyType" label="Company" />
												<SelectValueFormField
													{...props}
													placeholder={t('Year')}
													name="year"
													label="Year"
													options={[0].reduce((p, c) => {
														let lp = p;
														let i = 2021;
														while (i <= moment().year()) {
															lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
															i = i + 1;
														}
														return lp?.reverse();
													}, [])}
												/>
												<SelectValueFormField
													{...props}
													placeholder={t('Risk Ranking')}
													name="level"
													label="Risk Ranking"
													options={riskLevels?.map((e) => ({
														label: `${e?.label} (Code: ${e?.code || '—'} Score: ${e?.score || '—'} Status: ${e?.status || '—'})`,
														value: e?.value,
													}))}
												/>
												<Flex mt={2} justifyContent="flex-end">
													<Button
														mr={2}
														colorScheme="gray"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														borderRadius={c.borderRadius}
														onClick={() => {
															onCallback('?page=1');
															onClose();
														}}
													>
														{t('Clear')}
													</Button>
													<Button
														ml={2}
														colorScheme="blue"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														type="submit"
														borderRadius={c.borderRadius}
													>
														{t('Apply')}
													</Button>
												</Flex>
											</SimpleGrid>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}
